import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import Hero from "./Hero";
import About from "./About";
import Products from "./Products";
import Features from "./Features";
import Process from "./Process";
import Contact from "./Contact";
import Footer from "./Footer";
import ProductCatalog from "./ProductCatalog";
import AboutPage from "./AboutPage";
import WhatsAppButton from "./WhatsAppButton";

export default function PublicLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === "/" || location.pathname === "";
  const isProducts = location.pathname === "/products";
  const isAbout = location.pathname === "/about";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToSection = (id) => {
    if (!isHome) {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 200);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
    setMobileOpen(false);
  };

  const navTo = (path) => { navigate(path); setMobileOpen(false); window.scrollTo({ top: 0 }); };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav shadow-sm" : "bg-transparent"}`} data-testid="main-navbar">
        <div className="container-custom">
          <div className="flex items-center justify-between h-16 md:h-20">
            <button onClick={() => navTo("/")} className="flex items-center gap-3 group" data-testid="logo-btn">
              <img src="/images/logo.png" alt="Sri Srinivasa Coir Industry" className="w-12 h-12 md:w-14 md:h-14 object-contain rounded-lg" />
              <div className="hidden sm:block">
                <span className="text-lg font-bold text-navy tracking-tight" style={{ fontFamily: 'DM Serif Display, serif' }}>Sri Srinivasa</span>
                <span className="text-lg font-bold text-forest tracking-tight" style={{ fontFamily: 'DM Serif Display, serif' }}> Coir Industry</span>
              </div>
            </button>

            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection("hero")} className={`text-sm font-medium transition-colors hover:text-forest ${isHome ? "text-forest" : "text-slate-700"}`} data-testid="nav-home">Home</button>
              <button onClick={() => navTo("/products")} className={`text-sm font-medium transition-colors hover:text-forest ${isProducts ? "text-forest" : "text-slate-700"}`} data-testid="nav-products">Products</button>
              <button onClick={() => navTo("/about")} className={`text-sm font-medium transition-colors hover:text-forest ${isAbout ? "text-forest" : "text-slate-700"}`} data-testid="nav-about">About Us</button>
              <button onClick={() => scrollToSection("contact")} className="text-sm font-medium transition-colors hover:text-forest text-slate-700" data-testid="nav-contact">Contact</button>
            </div>

            <div className="flex items-center gap-3">
              <button onClick={() => navigate(isAuthenticated ? "/portal" : "/portal/login")} className="hidden md:flex items-center gap-2 border-2 border-navy text-navy px-4 py-2 text-sm font-medium hover:bg-navy hover:text-white transition-all" data-testid="employee-login-btn">
                <User className="w-4 h-4" />
                {isAuthenticated ? "Portal" : "Employee Login"}
              </button>
              <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 rounded-lg hover:bg-slate-100" data-testid="mobile-menu-btn">
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-white border-t shadow-lg">
            <div className="container-custom py-4 space-y-1">
              <button onClick={() => scrollToSection("hero")} className="block w-full text-left py-3 px-4 rounded-lg text-slate-700 hover:bg-slate-50 font-medium">Home</button>
              <button onClick={() => navTo("/products")} className="block w-full text-left py-3 px-4 rounded-lg text-slate-700 hover:bg-slate-50 font-medium">Products</button>
              <button onClick={() => navTo("/about")} className="block w-full text-left py-3 px-4 rounded-lg text-slate-700 hover:bg-slate-50 font-medium">About Us</button>
              <button onClick={() => scrollToSection("contact")} className="block w-full text-left py-3 px-4 rounded-lg text-slate-700 hover:bg-slate-50 font-medium">Contact</button>
              <button onClick={() => navigate(isAuthenticated ? "/portal" : "/portal/login")} className="block w-full text-left py-3 px-4 rounded-lg text-forest font-medium border-t mt-2 pt-4">
                {isAuthenticated ? "Employee Portal" : "Employee Login"}
              </button>
            </div>
          </div>
        )}
      </nav>

      <main className="pt-16 md:pt-20">
        {isHome && (
          <>
            <section id="hero"><Hero onExploreProducts={() => navTo("/products")} /></section>
            <section id="about"><About /></section>
            <section id="products"><Products onViewAll={() => navTo("/products")} /></section>
            <Features />
            <Process />
            <section id="contact"><Contact /></section>
          </>
        )}
        {isProducts && <ProductCatalog />}
        {isAbout && <AboutPage />}
        <Footer onNavigate={navTo} />
      </main>

      <WhatsAppButton />
    </div>
  );
}
